import { useEffect, useState } from 'react';

import type { MantineTheme } from '@mantine/core';
import { Divider, Group, Text, useMantineTheme } from '@mantine/core';

import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { Button } from './UI/Button/Button';
import { UserWithAvatar } from './UI/UserWithAvatar/UserWithAvatar';

import {
  type CollaborationTabValues,
  Header,
} from '../../features/collaboration/components/Header';
import { useUrlHash } from '../contexts/UrlHash.provider';
import { Permission } from '../entities/Access';
import type Tender from '../entities/Tender';
import type { User } from '../entities/User';
import { useUpdateOwnerMutation } from '../hooks/useUpdateOwnerMutation.hook';
import { useWorkspaceUsersQuery } from '../hooks/useWorkspaceUsersQuery.hook';
import { UrlHashTypes } from '../utils/navigation/UrlHashBuilder';
import { PermissionGuard } from './PermissionGuard';
import { WorkSpaceUsersSelect } from './WorkSpaceUsersSelect';

export type PreviousPageData = {
  pathname: string;
  title: string;
};

type HeaderActionBarProps = {
  owner?: User | null;
  setActiveTab?: (tab: CollaborationTabValues | null) => void;
  isSidePanelOppenned?: boolean;
  tender?: Tender;
};

export function HeaderActionBar({
  owner,
  setActiveTab,
  isSidePanelOppenned,
  tender,
}: HeaderActionBarProps) {
  return (
    <Group
      noWrap
      position="apart"
      h="08"
      sx={theme => ({
        flexDirection: 'row-reverse',
        paddingRight: isSidePanelOppenned
          ? theme.spacing['04']
          : theme.spacing['03'],
      })}
    >
      <ActionGroup
        owner={owner}
        setActiveTab={setActiveTab}
        isSidePanelOppenned={isSidePanelOppenned}
        tender={tender}
      />
      <ReturnButton />
    </Group>
  );
}

const ReturnButton = () => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { getReceivedUrlHashParam } = useUrlHash();
  const locationDataFromUrl = getReceivedUrlHashParam(
    UrlHashTypes.LOCATION_DATA,
  );

  const [locationData, setLocationData] = useState<PreviousPageData | null>(
    locationDataFromUrl,
  );

  useEffect(() => {
    if (!locationData) {
      setLocationData(locationDataFromUrl);
    }
  }, [locationData, locationDataFromUrl]);

  if (!locationData) {
    return;
  }

  const { pathname: previousPathname, title: previousTitle } = locationData;

  return (
    <Button
      variant="white"
      maw="25%"
      c="gray"
      leftIcon={<IconArrowLeft size={16} />}
      onClick={() => {
        if (previousPathname) {
          navigate(previousPathname);
        }
      }}
      sx={(theme: MantineTheme) => ({
        ':hover': {
          color: theme.colors.gray[8],
        },
      })}
    >
      <Text
        variant="sm"
        fw={400}
        c="gray.6"
        truncate
        sx={{
          ':hover': {
            color: theme.colors.gray[8],
          },
        }}
      >
        {previousTitle}
      </Text>
    </Button>
  );
};

type ActionGroupProps = {
  owner: User | null | undefined;
  setActiveTab?: (tab: CollaborationTabValues | null) => void;
  isSidePanelOppenned?: boolean;
  tender?: Tender;
};

const ActionGroup = ({
  owner: initialOwner,
  setActiveTab,
  isSidePanelOppenned,
  tender,
}: ActionGroupProps) => {
  const displaySidePanelHeader = !isSidePanelOppenned && setActiveTab && tender;
  const usersQuery = useWorkspaceUsersQuery();
  const [owner, setOwner] = useState<User | null>(initialOwner || null);
  const displayDivider = owner && usersQuery.data && !isSidePanelOppenned;
  const { mutate: updateOwnerMutation } = useUpdateOwnerMutation();

  const onOwnerChange = (ownerId: string) => {
    const newOwner = usersQuery.data?.find(user => user.id === ownerId);
    if (!newOwner) {
      throw new Error('New owner not found');
    }
    tender?.id && updateOwnerMutation({ ownerId, tenderId: tender.id });
    setOwner(newOwner);
  };

  return (
    <Group noWrap spacing="02">
      <PermissionGuard
        permission={Permission.UPDATE_OWNER}
        fallback={
          owner && (
            <UserWithAvatar
              avatarColor={owner.avatarColor}
              firstName={owner.firstName}
              lastName={owner.lastName}
              isDisabled={!!owner.disabledAt}
              w="fit-content"
            />
          )
        }
      >
        <WorkSpaceUsersSelect
          variant="unstyled"
          user={owner}
          placeholder="Assigner un responsable"
          withCrown
          onChange={onOwnerChange}
        />
      </PermissionGuard>

      {displayDivider && (
        <Divider orientation="vertical" h="04" my="auto" color="gray.2" />
      )}
      {displaySidePanelHeader && (
        <Header setActiveTab={setActiveTab} oppened={false} />
      )}
    </Group>
  );
};
